import {
  DeesElement,
  type TemplateResult,
  property,
  customElement,
  html,
  css,
  cssManager,
  state,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import * as interfaces from '../../interfaces/index.js';
import type { DeesAppuiBar } from '../dees-appui-appbar/index.js';
import type { DeesAppuiMainmenu } from '../dees-appui-mainmenu/dees-appui-mainmenu.js';
import type { DeesAppuiSecondarymenu } from '../dees-appui-secondarymenu/dees-appui-secondarymenu.js';
import type { DeesAppuiMaincontent } from '../dees-appui-maincontent/dees-appui-maincontent.js';
import type { DeesAppuiActivitylog } from '../dees-appui-activitylog/dees-appui-activitylog.js';
import type { DeesAppuiBottombar } from '../dees-appui-bottombar/dees-appui-bottombar.js';
import { demoFunc } from './dees-appui.demo.js';
import { themeDefaultStyles } from '../../00theme.js';
import { zIndexLayers } from '../../00zindex.js';

// View registry for managing views
import { ViewRegistry } from './view.registry.js';

// Import child components
import '../dees-appui-appbar/index.js';
import '../dees-appui-bottombar/dees-appui-bottombar.js';
import '../dees-appui-mainmenu/dees-appui-mainmenu.js';
import '../dees-appui-secondarymenu/dees-appui-secondarymenu.js';
import '../dees-appui-maincontent/dees-appui-maincontent.js';
import '../dees-appui-activitylog/dees-appui-activitylog.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-appui': DeesAppui;
  }
}

@customElement('dees-appui')
export class DeesAppui extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['App UI'];

  // ==========================================
  // REACTIVE OBSERVABLES (RxJS Subjects)
  // ==========================================

  /** Observable stream of view lifecycle events */
  public viewLifecycle$ = new domtools.plugins.smartrx.rxjs.Subject<interfaces.IViewLifecycleEvent>();

  /** Observable stream of view change events */
  public viewChanged$ = new domtools.plugins.smartrx.rxjs.Subject<interfaces.IViewChangeEvent>();

  // ==========================================
  // INTERNAL PROPERTIES (Properties for child components)
  // ==========================================

  // Properties for appbar
  @property({ type: Array })
  accessor appbarMenuItems: interfaces.IAppBarMenuItem[] = [];

  @property({ type: String })
  accessor appbarBreadcrumbs: string = '';

  @property({ type: String })
  accessor appbarBreadcrumbSeparator: string = ' > ';

  @property({ type: Boolean }) accessor appbarLocationEnabled = false;
  @state() private accessor locationItems: interfaces.IAppLocationItem[] = [];
  @state() private accessor currentRoute: interfaces.IAppRoute | undefined;
  private locationBaseUrl = '';
  private readonly openLocation = (href: string) => this.navigateToUrl(href);


  @property({ type: Boolean })
  accessor appbarShowWindowControls: boolean = true;

  @property({ type: Object })
  accessor appbarUser: interfaces.IAppUser | undefined = undefined;

  @property({ type: Array })
  accessor appbarProfileMenuItems: interfaces.IAppBarMenuItem[] = [];

  @property({ type: Boolean })
  accessor appbarShowSearch: boolean = false;

  // Properties for mainmenu
  @property({ type: String })
  accessor mainmenuLogoIcon: string = '';

  @property({ type: String })
  accessor mainmenuLogoText: string = '';

  @property({ type: Array })
  accessor mainmenuGroups: interfaces.IMenuGroup[] = [];

  @property({ type: Array })
  accessor mainmenuBottomTabs: interfaces.IMenuItem[] = [];

  @property({ type: Array })
  accessor mainmenuTabs: interfaces.IMenuItem[] = [];

  @property({ type: Object })
  accessor mainmenuSelectedTab: interfaces.IMenuItem | undefined = undefined;

  // Properties for secondarymenu
  @property({ type: String })
  accessor secondarymenuHeading: string = '';

  @property({ type: Array })
  accessor secondarymenuGroups: interfaces.ISecondaryMenuGroup[] = [];

  @property({ type: Object })
  accessor secondarymenuSelectedItem: interfaces.ISecondaryMenuItemTab | undefined = undefined;

  // Collapse states
  @property({ type: Boolean })
  accessor mainmenuCollapsed: boolean = false;

  @property({ type: Boolean })
  accessor secondarymenuCollapsed: boolean = false;

  // Visibility states
  @property({ type: Boolean })
  accessor mainmenuVisible: boolean = true;

  @property({ type: Boolean })
  accessor secondarymenuVisible: boolean = true;

  @property({ type: Boolean })
  accessor maincontentTabsVisible: boolean = true;

  @property({ type: Boolean })
  accessor contentTabsAutoHide: boolean = false;

  @property({ type: Number })
  accessor contentTabsAutoHideThreshold: number = 0;

  // Activity log visibility and count
  @state()
  accessor activityLogVisible: boolean = false;

  @property({ type: Boolean })
  accessor activityLogLive: boolean = false;

  @state()
  accessor activityLogCount: number = 0;

  // Properties for maincontent
  @property({ type: Array })
  accessor maincontentTabs: interfaces.IMenuItem[] = [];

  @property({ type: Object })
  accessor maincontentSelectedTab: interfaces.IMenuItem | undefined = undefined;

  @property({ type: Array })
  accessor contentTabActionsLeft: interfaces.ITabAction[] = [];

  @property({ type: Array })
  accessor contentTabActionsRight: interfaces.ITabAction[] = [];

  // References to child components
  @state()
  accessor appbar: DeesAppuiBar | undefined = undefined;

  @state()
  accessor mainmenu: DeesAppuiMainmenu | undefined = undefined;

  @state()
  accessor secondarymenu: DeesAppuiSecondarymenu | undefined = undefined;

  @state()
  accessor maincontent: DeesAppuiMaincontent | undefined = undefined;

  @state()
  accessor activitylogElement: DeesAppuiActivitylog | undefined = undefined;

  @state()
  accessor bottombarElement: DeesAppuiBottombar | undefined = undefined;

  @state()
  accessor bottombarVisible: boolean = true;

  // Current view state
  @state()
  accessor currentView: interfaces.IViewDefinition | undefined = undefined;

  // Internal services
  private viewRegistry: ViewRegistry = new ViewRegistry();
  private routerCleanup: (() => void) | null = null;
  private searchCallback: ((query: string) => void) | null = null;

  @state() private accessor compact = false;
  @state() private accessor compactMainOpen = false;
  @state() private accessor compactSecondaryOpen = false;

  constructor() {
    super();
    this.registerStartupFunction(async (signal) => {
      await this.updateComplete;
      if (signal.aborted) return;
      const update = () => { this.compact = this.clientWidth <= 700; };
      update();
      const observer = new ResizeObserver(update);
      observer.observe(this);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    });
  }

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      @media (prefers-reduced-motion: reduce) {
        *,
        *::before,
        *::after {
          transition: none !important;
          animation: none !important;
        }
      }

      :host {
        --appui-bar-height: var(--dees-appui-row-height);
        --appui-status-height: 24px;
        position: absolute;
        height: 100%;
        width: 100%;
        container-type: inline-size;
        background: var(--dees-color-bg-canvas);
      }
      .maingrid {
        position: absolute;
        top: var(--appui-bar-height);
        /* Shared chrome row above, compact status bar below. */
        height: calc(100% - var(--appui-bar-height) - var(--appui-status-height));
        width: 100%;
        display: grid;
        /* grid-template-columns set dynamically in template */
        grid-template-rows: 1fr;
        transition: grid-template-columns 0.3s ease, height 0.3s ease;
        overflow: clip;
      }

      :host([bottombar-hidden]) .maingrid {
        height: calc(100% - var(--appui-bar-height));
      }

      /* Keep appbar controls above the workspace content. */
      dees-appui-appbar {
        position: relative;
        z-index: ${zIndexLayers.overlay.dropdown};
      }

      dees-appui-bottombar {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 4;
      }
      dees-appui-bottombar[hidden] { display: none; }

      /* Z-index layering for proper stacking */
      .maingrid > dees-appui-mainmenu {
        position: relative;
        z-index: 3;
        min-height: 0;
      }

      .maingrid > dees-appui-secondarymenu {
        position: relative;
        z-index: 2;
        min-height: 0;
      }

      .maingrid > dees-appui-maincontent {
        position: relative;
        z-index: 1;
        min-height: 0;
        min-width: 0;
      }

      .maingrid > dees-appui-activitylog {
        position: relative;
        z-index: 1;
        overflow: hidden;
        /* The grid track reveals this fixed-width panel as it slides in. Its
           contents never inherit the track's animated 0–280px width. */
        width: 280px;
        justify-self: start;
        visibility: visible;
        transition: visibility 0s linear;
      }

      .maingrid > dees-appui-activitylog.hidden {
        visibility: hidden;
        transition-delay: 0.3s;
        pointer-events: none;
      }

      @media (pointer: coarse) {
        :host { --appui-status-height: 44px; }
      }

      @container (max-width: 700px) {
        .maingrid > dees-appui-mainmenu,
        .maingrid > dees-appui-secondarymenu {
          position: absolute;
          left: 0;
          top: var(--dees-appui-row-height);
          bottom: 0;
          height: auto;
          overflow: visible;
          z-index: 7;
          box-shadow: var(--dees-shadow-md);
        }

        .maingrid > dees-appui-secondarymenu {
          width: 240px;
        }

        .maingrid > dees-appui-mainmenu {
          width: 200px;
        }

        .maingrid > dees-appui-mainmenu[inert],
        .maingrid > dees-appui-secondarymenu[inert] {
          visibility: hidden;
          pointer-events: none;
        }

        .maingrid > dees-appui-activitylog {
          position: absolute;
          right: 0;
          top: var(--dees-appui-row-height);
          bottom: 0;
          height: auto;
          width: 100%;
          z-index: 5;
          box-shadow: var(--dees-shadow-md);
          transform: translateX(0);
          transition: transform 0.3s ease, visibility 0s linear;
        }

        .maingrid > dees-appui-activitylog.hidden {
          transform: translateX(100%);
          transition-delay: 0s, 0.3s;
        }

        .maingrid {
          grid-template-columns: minmax(0, 1fr) !important;
          grid-template-rows: auto minmax(0, 1fr);
          transition: none;
        }

        .compact-navigation {
          display: flex !important;
          align-items: center;
          gap: 8px;
          height: var(--dees-appui-row-height);
          padding-inline: 8px;
          border-bottom: 1px solid var(--dees-color-border-subtle);
          background: var(--dees-appui-chrome-bg);
        }

        .compact-navigation button {
          display: flex;
          align-items: center;
          gap: 8px;
          min-height: calc(var(--dees-appui-row-height) - 4px);
          padding: 0 10px;
          border: 0;
          border-radius: var(--dees-radius-md);
          font: 500 13px var(--dees-font-family);
          color: var(--dees-color-text-secondary);
          background: transparent;
          cursor: pointer;
        }

        .compact-navigation button[aria-expanded='true'] {
          color: var(--dees-color-accent-primary);
          background: var(--dees-color-nav-selected);
        }

        .compact-navigation button:focus-visible {
          outline: 2px solid var(--dees-color-focus-ring);
          outline-offset: -2px;
        }

        .compact-nav-backdrop {
          display: block !important;
          position: absolute;
          inset: var(--dees-appui-row-height) 0 0;
          z-index: 6;
          border: 0;
          background: var(--dees-color-scrim);
        }
      }

      .compact-navigation,
      .compact-nav-backdrop {
        display: none;
      }

      /* View container for dynamically loaded views */
      .view-container {
        display: contents;
      }

      .view-container:empty {
        display: none;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <dees-appui-appbar
        .menuItems=${this.appbarMenuItems}
        .breadcrumbs=${this.appbarBreadcrumbs}
        .breadcrumbSeparator=${this.appbarBreadcrumbSeparator}
        .location=${this.appbarLocationEnabled && this.currentRoute
          ? {
              href: this.createViewUrl(
                this.currentRoute.viewId,
                this.currentRoute.params,
                this.currentRoute.query,
              ),
              items: this.locationItems.length
                ? this.locationItems
                : [{ label: this.currentView?.name || this.currentRoute.viewId }],
              navigate: this.openLocation,
            }
          : undefined}
        .showWindowControls=${this.appbarShowWindowControls}
        .user=${this.appbarUser}
        .profileMenuItems=${this.appbarProfileMenuItems}
        .showSearch=${this.appbarShowSearch}
        .showActivityLogToggle=${true}
        .activityLogCount=${this.activityLogCount}
        .activityLogActive=${this.activityLogVisible}
        @menu-select=${(e: CustomEvent) => this.handleAppbarMenuSelect(e)}
        @breadcrumb-navigate=${(e: CustomEvent) => this.handleAppbarBreadcrumbNavigate(e)}
        @search-click=${() => this.handleAppbarSearchClick()}
        @search-query=${(e: CustomEvent) => this.handleAppbarSearchQuery(e)}
        @user-menu-open=${() => this.handleAppbarUserMenuOpen()}
        @profile-menu-select=${(e: CustomEvent) => this.handleAppbarProfileMenuSelect(e)}
        @activity-toggle=${() => this.toggleActivityLog()}
      ></dees-appui-appbar>
      <div
        class="maingrid"
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Escape' && this.compact && (this.compactMainOpen || this.compactSecondaryOpen)) {
            e.preventDefault();
            this.closeCompactMenus();
          }
        }}
        style="grid-template-columns: ${
          this.mainmenuVisible ? 'auto ' : ''
        }${
          this.secondarymenuVisible ? 'auto ' : ''
        }minmax(0, 1fr) ${
          this.activityLogVisible ? '280px' : '0px'
        };"
      >
        <nav class="compact-navigation" aria-label="Workspace navigation">
          ${this.mainmenuVisible
            ? html`<button
                type="button"
                data-menu="main"
                aria-expanded=${this.compactMainOpen}
                @click=${() => this.toggleCompactMenu('main')}
              ><dees-icon icon="lucide:PanelLeft" iconSize="16"></dees-icon>Navigation</button>`
            : ''}
          ${this.secondarymenuVisible
            ? html`<button
                type="button"
                data-menu="section"
                aria-expanded=${this.compactSecondaryOpen}
                @click=${() => this.toggleCompactMenu('section')}
              ><dees-icon icon="lucide:ListFilter" iconSize="16"></dees-icon>${this.secondarymenuHeading}</button>`
            : ''}
        </nav>
        ${this.compact && (this.compactMainOpen || this.compactSecondaryOpen)
          ? html`<button
              class="compact-nav-backdrop"
              type="button"
              tabindex="-1"
              aria-label="Close navigation"
              @click=${() => this.closeCompactMenus()}
            ></button>`
          : ''}
        ${this.mainmenuVisible ? html`
          <dees-appui-mainmenu
            ?inert=${this.compact && !this.compactMainOpen}
            .logoIcon=${this.mainmenuLogoIcon}
            .logoText=${this.mainmenuLogoText}
            .menuGroups=${this.mainmenuGroups}
            .bottomTabs=${this.mainmenuBottomTabs}
            .tabs=${this.mainmenuTabs}
            .selectedTab=${this.mainmenuSelectedTab}
            .collapsed=${this.compact ? !this.compactMainOpen : this.mainmenuCollapsed}
            @tab-select=${(e: CustomEvent) => this.handleMainmenuTabSelect(e)}
            @collapse-change=${(e: CustomEvent) => this.handleMainmenuCollapseChange(e)}
          ></dees-appui-mainmenu>
        ` : ''}
        ${this.secondarymenuVisible ? html`
          <dees-appui-secondarymenu
            ?inert=${this.compact && !this.compactSecondaryOpen}
            .heading=${this.secondarymenuHeading}
            .groups=${this.secondarymenuGroups}
            .selectedItem=${this.secondarymenuSelectedItem}
            .collapsed=${this.compact ? !this.compactSecondaryOpen : this.secondarymenuCollapsed}
            @item-select=${(e: CustomEvent) => this.handleSecondarymenuItemSelect(e)}
            @collapse-change=${(e: CustomEvent) => this.handleSecondarymenuCollapseChange(e)}
          ></dees-appui-secondarymenu>
        ` : ''}
        <dees-appui-maincontent
          ?inert=${this.compact
            && (this.compactMainOpen || this.compactSecondaryOpen || this.activityLogVisible)}
          .tabs=${this.maincontentTabs}
          .selectedTab=${this.maincontentSelectedTab}
          .showTabs=${this.maincontentTabsVisible}
          .tabsAutoHide=${this.contentTabsAutoHide}
          .tabsAutoHideThreshold=${this.contentTabsAutoHideThreshold}
          .tabActionsLeft=${this.contentTabActionsLeft}
          .tabActionsRight=${this.contentTabActionsRight}
          @tab-select=${(e: CustomEvent) => this.handleContentTabSelect(e)}
          @tab-close=${(e: CustomEvent) => this.handleContentTabClose(e)}
        >
          <div class="view-container"></div>
          <slot name="maincontent"></slot>
        </dees-appui-maincontent>
        <dees-appui-activitylog
          .live=${this.activityLogLive}
          class="${this.activityLogVisible ? 'visible' : 'hidden'}"
          ?inert=${!this.activityLogVisible}
          @close-request=${() => this.setActivityLogVisible(false)}
        ></dees-appui-activitylog>
      </div>
      <dees-appui-bottombar ?hidden=${!this.bottombarVisible}></dees-appui-bottombar>
    `;
  }

  async firstUpdated() {
    // Get references to child components
    this.appbar = this.shadowRoot!.querySelector('dees-appui-appbar') as DeesAppuiBar;
    this.mainmenu = this.shadowRoot!.querySelector('dees-appui-mainmenu') as DeesAppuiMainmenu;
    this.secondarymenu = this.shadowRoot!.querySelector('dees-appui-secondarymenu') as DeesAppuiSecondarymenu;
    this.maincontent = this.shadowRoot!.querySelector('dees-appui-maincontent') as DeesAppuiMaincontent;
    this.activitylogElement = this.shadowRoot!.querySelector(
      'dees-appui-activitylog',
    ) as DeesAppuiActivitylog;
    this.bottombarElement = this.shadowRoot!.querySelector('dees-appui-bottombar') as DeesAppuiBottombar;

    // Subscribe to activity log entry changes for badge count
    if (this.activitylogElement) {
      this.activitylogElement.entries$.subscribe((entries) => {
        this.activityLogCount = entries.length;
      });
    }

    // Set appui reference in view registry for lifecycle context
    this.viewRegistry.setAppuiRef(this as unknown as interfaces.TDeesAppui);
  }

  async disconnectedCallback() {
    await super.disconnectedCallback();
    // Clean up router listener
    if (this.routerCleanup) {
      this.routerCleanup();
      this.routerCleanup = null;
    }
    // Complete subjects
    this.viewLifecycle$.complete();
    this.viewChanged$.complete();
  }

  // ==========================================
  // PROGRAMMATIC API: APP BAR
  // ==========================================

  /**
   * Set the app bar menu items (File, Edit, View, etc.)
   */
  public setAppBarMenus(menus: interfaces.IAppBarMenuItem[]): void {
    this.appbarMenuItems = [...menus];
  }

  /**
   * Update a single app bar menu by name
   */
  public updateAppBarMenu(name: string, update: Partial<interfaces.IAppBarMenuItem>): void {
    this.appbarMenuItems = this.appbarMenuItems.map(menu => {
      // Check if it's not a divider and has a name property
      if ('name' in menu && menu.name === name) {
        return { ...menu, ...update };
      }
      return menu;
    });
  }

  /**
   * Set the breadcrumbs (string or array)
   */
  public setBreadcrumbs(breadcrumbs: string | string[]): void {
    if (Array.isArray(breadcrumbs)) {
      this.appbarBreadcrumbs = breadcrumbs.join(this.appbarBreadcrumbSeparator);
    } else {
      this.appbarBreadcrumbs = breadcrumbs;
    }
  }

  /**
   * Set the current user
   */
  public setUser(user: interfaces.IAppUser | undefined): void {
    this.appbarUser = user;
  }

  /**
   * Set the profile dropdown menu items
   */
  public setProfileMenuItems(items: interfaces.IAppBarMenuItem[]): void {
    this.appbarProfileMenuItems = [...items];
  }

  /**
   * Set search bar visibility
   */
  public setSearchVisible(visible: boolean): void {
    this.appbarShowSearch = visible;
  }

  /**
   * Set window controls visibility
   */
  public setWindowControlsVisible(visible: boolean): void {
    this.appbarShowWindowControls = visible;
  }

  /**
   * Register a search callback
   */
  public onSearch(callback: (query: string) => void): void {
    this.searchCallback = callback;
  }

  // ==========================================
  // PROGRAMMATIC API: MAIN MENU
  // ==========================================

  /**
   * Set the entire main menu configuration
   */
  public setMainMenu(config: interfaces.IMainMenuConfig): void {
    if (config.logoIcon !== undefined) {
      this.mainmenuLogoIcon = config.logoIcon;
    }
    if (config.logoText !== undefined) {
      this.mainmenuLogoText = config.logoText;
    }
    if (config.groups !== undefined) {
      this.mainmenuGroups = [...config.groups];
    }
    if (config.bottomTabs !== undefined) {
      this.mainmenuBottomTabs = [...config.bottomTabs];
    }
  }

  /**
   * Update a specific menu group by name
   */
  public updateMainMenuGroup(groupName: string, update: Partial<interfaces.IMenuGroup>): void {
    this.mainmenuGroups = this.mainmenuGroups.map(group =>
      group.name === groupName ? { ...group, ...update } : group
    );
  }

  /**
   * Add a menu item to a specific group
   */
  public addMainMenuItem(groupName: string, tab: interfaces.IMenuItem): void {
    this.mainmenuGroups = this.mainmenuGroups.map(group => {
      if (group.name === groupName) {
        return {
          ...group,
          items: [...(group.items || []), tab],
        };
      }
      return group;
    });
  }

  /**
   * Remove a menu item from a group by key
   */
  public removeMainMenuItem(groupName: string, tabKey: string): void {
    this.mainmenuGroups = this.mainmenuGroups.map(group => {
      if (group.name === groupName) {
        return {
          ...group,
          items: (group.items || []).filter(t => t.key !== tabKey),
        };
      }
      return group;
    });
  }

  /**
   * Set the selected main menu item by key
   */
  public setMainMenuSelection(tabKey: string): void {
    for (const group of this.mainmenuGroups) {
      const tab = group.items?.find(t => t.key === tabKey);
      if (tab) {
        this.mainmenuSelectedTab = tab;
        return;
      }
    }
    // Check bottom tabs
    const bottomTab = this.mainmenuBottomTabs.find(t => t.key === tabKey);
    if (bottomTab) {
      this.mainmenuSelectedTab = bottomTab;
    }
  }

  /**
   * Set main menu collapsed state
   */
  public setMainMenuCollapsed(collapsed: boolean): void {
    this.mainmenuCollapsed = collapsed;
  }

  /**
   * Set main menu visibility
   */
  public setMainMenuVisible(visible: boolean): void {
    this.mainmenuVisible = visible;
  }

  /**
   * Set secondary menu collapsed state
   */
  public setSecondaryMenuCollapsed(collapsed: boolean): void {
    this.secondarymenuCollapsed = collapsed;
  }

  /**
   * Set secondary menu visibility
   */
  public setSecondaryMenuVisible(visible: boolean): void {
    this.secondarymenuVisible = visible;
  }

  /**
   * Set content tabs visibility
   */
  public setContentTabsVisible(visible: boolean): void {
    this.maincontentTabsVisible = visible;
  }

  /**
   * Set content tabs auto-hide behavior
   * @param enabled - Enable auto-hide feature
   * @param threshold - Hide when tabs.length <= threshold (default 0 = hide when no tabs)
   */
  public setContentTabsAutoHide(enabled: boolean, threshold: number = 0): void {
    this.contentTabsAutoHide = enabled;
    this.contentTabsAutoHideThreshold = threshold;
  }

  /**
   * Set a badge on a main menu item
   */
  public setMainMenuBadge(tabKey: string, badge: string | number): void {
    this.mainmenuGroups = this.mainmenuGroups.map(group => ({
      ...group,
      items: (group.items || []).map(tab =>
        tab.key === tabKey ? { ...tab, badge } : tab
      ),
    }));
    // Also check bottom tabs
    this.mainmenuBottomTabs = this.mainmenuBottomTabs.map(tab =>
      tab.key === tabKey ? { ...tab, badge } : tab
    );
  }

  /**
   * Clear a badge from a main menu item
   */
  public clearMainMenuBadge(tabKey: string): void {
    this.mainmenuGroups = this.mainmenuGroups.map(group => ({
      ...group,
      items: (group.items || []).map(tab => {
        if (tab.key === tabKey) {
          const { badge, ...rest } = tab;
          return rest;
        }
        return tab;
      }),
    }));
    // Also check bottom tabs
    this.mainmenuBottomTabs = this.mainmenuBottomTabs.map(tab => {
      if (tab.key === tabKey) {
        const { badge, ...rest } = tab;
        return rest;
      }
      return tab;
    });
  }

  // ==========================================
  // PROGRAMMATIC API: SECONDARY MENU
  // ==========================================

  /**
   * Set the secondary menu configuration
   */
  public setSecondaryMenu(config: { heading?: string; groups: interfaces.ISecondaryMenuGroup[] }): void {
    if (config.heading !== undefined) {
      this.secondarymenuHeading = config.heading;
    }
    this.secondarymenuGroups = [...config.groups];
  }

  /**
   * Update a specific secondary menu group
   */
  public updateSecondaryMenuGroup(groupName: string, update: Partial<interfaces.ISecondaryMenuGroup>): void {
    this.secondarymenuGroups = this.secondarymenuGroups.map(group =>
      group.name === groupName ? { ...group, ...update } : group
    );
  }

  /**
   * Add an item to a secondary menu group
   */
  public addSecondaryMenuItem(
    groupName: string,
    item: interfaces.ISecondaryMenuItem
  ): void {
    this.secondarymenuGroups = this.secondarymenuGroups.map(group => {
      if (group.name === groupName) {
        return {
          ...group,
          items: [...group.items, item],
        };
      }
      return group;
    });
  }

  /**
   * Set the selected secondary menu item by key (for tab items only)
   */
  public setSecondaryMenuSelection(itemKey: string): void {
    for (const group of this.secondarymenuGroups) {
      const item = group.items.find(i => 'key' in i && i.key === itemKey);
      if (item && (!('type' in item) || item.type === 'tab' || item.type === undefined)) {
        this.secondarymenuSelectedItem = item as interfaces.ISecondaryMenuItemTab;
        return;
      }
    }
  }

  /**
   * Clear the secondary menu
   */
  public clearSecondaryMenu(): void {
    this.secondarymenuHeading = '';
    this.secondarymenuGroups = [];
    this.secondarymenuSelectedItem = undefined;
  }

  // ==========================================
  // PROGRAMMATIC API: CONTENT TABS
  // ==========================================

  /**
   * Set the content tabs
   */
  public setContentTabs(tabs: interfaces.IMenuItem[]): void {
    this.maincontentTabs = [...tabs];
    if (tabs.length > 0 && !this.maincontentSelectedTab) {
      this.maincontentSelectedTab = tabs[0];
    }
  }

  /**
   * Add a content tab. A key is an identity: adding a key that is already open
   * replaces that tab's descriptor in place instead of opening a twin, because
   * selection, removal and rendering all address a tab by its key. Use
   * `selectContentTab()` to bring a tab to the front.
   */
  public addContentTab(tab: interfaces.IMenuItem): void {
    const index = this.maincontentTabs.findIndex(t => t.key === tab.key);
    if (index === -1) {
      this.maincontentTabs = [...this.maincontentTabs, tab];
      return;
    }
    this.replaceContentTabAt(index, tab);
  }

  /**
   * Update an open content tab, for example to rename it once the record it
   * shows is known. Returns whether the key was open; order and selection are
   * preserved.
   */
  public updateContentTab(tabKey: string, update: Partial<Omit<interfaces.IMenuItem, 'key'>>): boolean {
    const index = this.maincontentTabs.findIndex(t => t.key === tabKey);
    if (index === -1) return false;
    this.replaceContentTabAt(index, { ...this.maincontentTabs[index], ...update });
    return true;
  }

  /** Swap one tab descriptor for the same key, keeping its position and selection. */
  private replaceContentTabAt(index: number, tab: interfaces.IMenuItem): void {
    const tabs = [...this.maincontentTabs];
    tabs[index] = tab;
    this.maincontentTabs = tabs;
    if (this.maincontentSelectedTab?.key === tab.key) {
      this.maincontentSelectedTab = tab;
    }
  }

  /**
   * Remove a content tab by key
   */
  public removeContentTab(tabKey: string): void {
    this.maincontentTabs = this.maincontentTabs.filter(t => t.key !== tabKey);
    if (this.maincontentSelectedTab?.key === tabKey) {
      this.maincontentSelectedTab = this.maincontentTabs[0];
    }
  }

  /**
   * Select a content tab by key
   */
  public selectContentTab(tabKey: string): void {
    const tab = this.maincontentTabs.find(t => t.key === tabKey);
    if (tab) {
      this.maincontentSelectedTab = tab;
    }
  }

  /**
   * Get the currently selected content tab
   */
  public getSelectedContentTab(): interfaces.IMenuItem | undefined {
    return this.maincontentSelectedTab;
  }

  /**
   * Get the open content tabs, in their displayed order. The array is a
   * snapshot: changing it does not change the tab bar.
   */
  public getContentTabs(): interfaces.IMenuItem[] {
    return [...this.maincontentTabs];
  }

  /**
   * Set content tab action buttons on the left side
   */
  public setContentTabActionsLeft(actions: interfaces.ITabAction[]): void {
    this.contentTabActionsLeft = [...actions];
  }

  /**
   * Set content tab action buttons on the right side
   */
  public setContentTabActionsRight(actions: interfaces.ITabAction[]): void {
    this.contentTabActionsRight = [...actions];
  }

  // ==========================================
  // PROGRAMMATIC API: ACTIVITY LOG
  // ==========================================

  /**
   * Get the activity log API
   */
  public get activityLog(): interfaces.IActivityLogAPI {
    if (!this.activitylogElement) {
      // Return a deferred API that will work after firstUpdated
      return {
        add: (entry) => {
          this.updateComplete.then(() => this.activitylogElement?.add(entry));
        },
        addMany: (entries) => {
          this.updateComplete.then(() => this.activitylogElement?.addMany(entries));
        },
        clear: () => {
          this.updateComplete.then(() => this.activitylogElement?.clear());
        },
        getEntries: () => this.activitylogElement?.getEntries() || [],
        filter: (criteria) => this.activitylogElement?.filter(criteria) || [],
        search: (query) => this.activitylogElement?.search(query) || [],
      };
    }
    return {
      add: (entry) => this.activitylogElement!.add(entry),
      addMany: (entries) => this.activitylogElement!.addMany(entries),
      clear: () => this.activitylogElement!.clear(),
      getEntries: () => this.activitylogElement!.getEntries(),
      filter: (criteria) => this.activitylogElement!.filter(criteria),
      search: (query) => this.activitylogElement!.search(query),
    };
  }

  /**
   * Set activity log visibility
   */
  public setActivityLogVisible(visible: boolean): void {
    if (!visible && this.activitylogElement?.shadowRoot?.activeElement) {
      this.appbar?.shadowRoot
        ?.querySelector<HTMLButtonElement>('.activity-toggle')
        ?.focus({ preventScroll: true });
    }
    this.activityLogVisible = visible;
  }

  /**
   * Toggle activity log visibility
   */
  public toggleActivityLog(): void {
    this.setActivityLogVisible(!this.activityLogVisible);
  }

  /**
   * Get activity log visibility state
   */
  public getActivityLogVisible(): boolean {
    return this.activityLogVisible;
  }

  // ==========================================
  // PROGRAMMATIC API: BOTTOM BAR
  // ==========================================

  /**
   * Get the bottom bar API for widget/action management
   */
  public get bottomBar(): interfaces.IBottomBarAPI {
    if (!this.bottombarElement) {
      // Return a deferred API that will work after firstUpdated
      return {
        addWidget: (widget) => {
          this.updateComplete.then(() => this.bottombarElement?.addWidget(widget));
        },
        updateWidget: (id, update) => {
          this.updateComplete.then(() => this.bottombarElement?.updateWidget(id, update));
        },
        removeWidget: (id) => {
          this.updateComplete.then(() => this.bottombarElement?.removeWidget(id));
        },
        getWidget: (id) => this.bottombarElement?.getWidget(id),
        clearWidgets: () => {
          this.updateComplete.then(() => this.bottombarElement?.clearWidgets());
        },
        addAction: (action) => {
          this.updateComplete.then(() => this.bottombarElement?.addAction(action));
        },
        removeAction: (id) => {
          this.updateComplete.then(() => this.bottombarElement?.removeAction(id));
        },
        clearActions: () => {
          this.updateComplete.then(() => this.bottombarElement?.clearActions());
        },
      };
    }
    return {
      addWidget: (widget) => this.bottombarElement!.addWidget(widget),
      updateWidget: (id, update) => this.bottombarElement!.updateWidget(id, update),
      removeWidget: (id) => this.bottombarElement!.removeWidget(id),
      getWidget: (id) => this.bottombarElement!.getWidget(id),
      clearWidgets: () => this.bottombarElement!.clearWidgets(),
      addAction: (action) => this.bottombarElement!.addAction(action),
      removeAction: (id) => this.bottombarElement!.removeAction(id),
      clearActions: () => this.bottombarElement!.clearActions(),
    };
  }

  /**
   * Set bottom bar visibility
   */
  public setBottomBarVisible(visible: boolean): void {
    this.bottombarVisible = visible;
    if (!visible) {
      this.setAttribute('bottombar-hidden', '');
    } else {
      this.removeAttribute('bottombar-hidden');
    }
  }

  /**
   * Get bottom bar visibility state
   */
  public getBottomBarVisible(): boolean {
    return this.bottombarVisible;
  }

  // ==========================================
  // PROGRAMMATIC API: NAVIGATION
  // ==========================================

  /**
   * Navigate to a view by ID
   */
  /** Readable ancestors for the optional location field. Each ancestor owns its URL. */
  public setLocationBreadcrumbs(items: interfaces.IAppLocationItem[]): void {
    this.locationItems = items;
  }

  public getCurrentRoute(): interfaces.IAppRoute | undefined {
    return this.currentRoute
      ? {
          ...this.currentRoute,
          params: { ...this.currentRoute.params },
          query: { ...this.currentRoute.query },
        }
      : undefined;
  }

  public createViewUrl(
    viewId: string,
    params: Record<string, string> = {},
    query: Record<string, string> = {},
  ): string {
    const url = new URL(this.locationBaseUrl || window.location.href);
    url.hash = this.viewRegistry.buildRoute(viewId, params, query);
    return url.href;
  }

  /** Accept links belonging to this hash-routed app, or a bare hash. Never leave the host. */
  public async navigateToUrl(href: string): Promise<boolean> {
    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      throw new Error('Enter a valid app link.');
    }
    if (
      !href.trim()
      || url.origin !== window.location.origin
      || url.pathname !== window.location.pathname
      || url.username
      || url.password
    ) {
      throw new Error('This link does not belong to this app.');
    }
    const route = this.viewRegistry.findByRoute(url.hash.slice(1));
    if (!route) throw new Error('This app screen or resource is unavailable. Check the link.');
    return this.navigateToView(route.view.id, route.params, route.query);
  }

  public navigateToView(
    viewId: string,
    params: Record<string, string> = {},
    query: Record<string, string> = {},
  ): Promise<boolean> {
    return this.queueNavigation(viewId, { ...params }, { ...query }, 'push');
  }

  private navigationQueue: Promise<boolean> = Promise.resolve(true);
  private routingGeneration = 0;
  private historyOwner = '';
  private historyIndex = 0;
  private restoringHistory?: { hash: string; resolve: () => void };

  private queueNavigation(
    viewId: string,
    params: Record<string, string>,
    query: Record<string, string>,
    mode: 'push' | 'replace' | 'traverse',
    index?: number,
    traversalHash?: string,
  ): Promise<boolean> {
    const generation = this.routingGeneration;
    const result = this.navigationQueue.then(async () => {
      if (!this.isConnected || generation !== this.routingGeneration) return false;
      return this.performNavigation(viewId, params, query, mode, generation, index, traversalHash);
    });
    this.navigationQueue = result.catch(() => false);
    return result;
  }

  private async performNavigation(
    viewId: string,
    params: Record<string, string>,
    query: Record<string, string>,
    mode: 'push' | 'replace' | 'traverse',
    generation: number,
    index?: number,
    traversalHash?: string,
  ): Promise<boolean> {
    const view = this.viewRegistry.get(viewId);
    if (!view) return false;
    let hash: string;
    try {
      hash = `#${this.viewRegistry.buildRoute(viewId, params, query)}`;
    } catch {
      return false;
    }
    if (!this.viewRegistry.findByRoute(hash.slice(1))) return false;
    if (this.currentRoute && hash === this.activeRouteHash) {
      if (mode !== 'push') {
        this.historyIndex = index ?? this.historyIndex;
        if (mode === 'replace' || window.location.hash === traversalHash)
          window.history.replaceState(this.routeHistoryState(), '', hash);
      }
      return true;
    }
    const canLeave = await this.viewRegistry.canLeaveCurrentView({ appui: this, viewId, params, query });
    if (!this.isConnected || generation !== this.routingGeneration) return false;
    if (canLeave !== true && (typeof canLeave !== 'string' || !window.confirm(canLeave))) {
      if (this.currentView) this.setMainMenuSelection(this.currentView.id);
      return false;
    }
    this.viewLifecycle$.next({ type: 'loading', viewId });
    try {
      await this.loadView(view, params, query);
      if (!this.isConnected || generation !== this.routingGeneration) return false;
      this.currentRoute = { viewId, params, query };
      this.activeRouteHash = hash;
      if (mode === 'push') {
        this.historyIndex++;
        window.history.pushState(this.routeHistoryState(), '', hash);
      } else {
        if (index !== undefined) this.historyIndex = index;
        // Traversal already moved the browser. Do not overwrite a newer queued traversal.
        if (mode === 'replace' || window.location.hash === traversalHash) {
          window.history.replaceState(this.routeHistoryState(), '', hash);
        }
      }
      return true;
    } catch (error) {
      this.viewLifecycle$.next({ type: 'loadError', viewId, error });
      return false;
    }
  }

  private routeHistoryState() {
    return { ...window.history.state, deesAppui: { owner: this.historyOwner, index: this.historyIndex } };
  }

  /**
   * Get the current view
   */
  public getCurrentView(): interfaces.IViewDefinition | undefined {
    return this.currentView;
  }

  /**
   * Get access to the view registry (for advanced use)
   */
  public getViewRegistry(): ViewRegistry {
    return this.viewRegistry;
  }

  // ==========================================
  // UNIFIED CONFIGURATION
  // ==========================================

  /**
   * Configure the app shell with a unified config object
   */
  public configure(config: interfaces.IAppConfig): void {
    // Register views
    if (config.views) {
      this.viewRegistry.clear();
      this.viewRegistry.registerAll(config.views);
    }

    // Apply branding
    if (config.branding) {
      this.mainmenuLogoIcon = config.branding.logoIcon || '';
      this.mainmenuLogoText = config.branding.logoText || '';
    }

    // Apply app bar config
    if (config.appBar) {
      this.appbarMenuItems = config.appBar.menuItems || [];
      this.appbarBreadcrumbs = config.appBar.breadcrumbs || '';
      this.appbarBreadcrumbSeparator = config.appBar.breadcrumbSeparator || ' > ';
      this.appbarLocationEnabled = config.appBar.location ?? false;
      this.locationBaseUrl = config.appBar.locationBaseUrl || '';
      if (this.locationBaseUrl) {
        const base = new URL(this.locationBaseUrl, window.location.href);
        if (
          base.origin !== window.location.origin
          || base.pathname !== window.location.pathname
          || base.username
          || base.password
        ) throw new Error('The location base URL must belong to this app.');
        base.hash = '';
        this.locationBaseUrl = base.href;
      }
      this.appbarShowWindowControls = config.appBar.showWindowControls ?? true;
      this.appbarShowSearch = config.appBar.showSearch ?? false;
      this.appbarUser = config.appBar.user;
      this.appbarProfileMenuItems = config.appBar.profileMenuItems || [];
    }

    // Build main menu from view references or direct config
    if (config.mainMenu) {
      if (config.mainMenu.sections) {
        this.mainmenuGroups = this.buildMainMenuFromSections(config);
      } else if (config.mainMenu.groups) {
        this.mainmenuGroups = config.mainMenu.groups;
      }

      if (config.mainMenu.logoIcon) {
        this.mainmenuLogoIcon = config.mainMenu.logoIcon;
      }
      if (config.mainMenu.logoText) {
        this.mainmenuLogoText = config.mainMenu.logoText;
      }
      if (config.mainMenu.bottomTabs) {
        this.mainmenuBottomTabs = config.mainMenu.bottomTabs;
      } else if (config.mainMenu.bottomItems) {
        this.mainmenuBottomTabs = this.buildBottomTabsFromItems(config.mainMenu.bottomItems);
      }
    }

    // Apply activity log config
    if (config.activityLog) {
      this.setActivityLogVisible(config.activityLog.visible ?? false);
      if (config.activityLog.live !== undefined) this.activityLogLive = config.activityLog.live;
    }

    // Apply bottom bar config
    if (config.bottomBar) {
      this.setBottomBarVisible(config.bottomBar.visible ?? true);

      if (config.bottomBar.widgets) {
        config.bottomBar.widgets.forEach(widget => {
          this.bottomBar.addWidget(widget);
        });
      }

      if (config.bottomBar.actions) {
        config.bottomBar.actions.forEach(action => {
          this.bottomBar.addAction(action);
        });
      }
    }

    // Setup domtools.router integration
    this.setupRouterIntegration();

    // Bind event callbacks
    if (config.onViewChange) {
      this.viewChanged$.subscribe((event) => {
        config.onViewChange!(event.viewId, event.view);
      });
    }

    if (config.onSearch) {
      this.searchCallback = config.onSearch;
    }

    // Resolve the initial route once; a deep link takes precedence over the default.
    const initialRoute = this.viewRegistry.findByRoute(window.location.hash.slice(1));
    const initialViewId = initialRoute?.view.id ?? config.defaultView;
    // Mark the menu before it first renders: its first-tab auto-selection would otherwise
    // queue a navigation that races the initial route and can replace a deep link.
    if (initialViewId) this.setMainMenuSelection(initialViewId);
    if (initialRoute) {
      void this.queueNavigation(initialRoute.view.id, initialRoute.params, initialRoute.query, 'replace');
    } else if (config.defaultView) {
      void this.queueNavigation(config.defaultView, {}, {}, 'replace');
    }
  }

  // ==========================================
  // PRIVATE HELPER METHODS
  // ==========================================

  private activeRouteHash = '';

  private setupRouterIntegration(): void {
    this.routerCleanup?.();
    this.currentRoute = undefined;
    this.locationItems = [];
    this.activeRouteHash = '';
    this.historyOwner = `${Date.now()}-${Math.random()}`;
    this.historyIndex = 0;
    window.history.replaceState(this.routeHistoryState(), '');
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (this.restoringHistory?.hash === hash) {
        const restoration = this.restoringHistory;
        this.restoringHistory = undefined;
        restoration.resolve();
        return;
      }
      const marker = window.history.state?.deesAppui;
      const index = marker?.owner === this.historyOwner ? marker.index : this.historyIndex + 1;
      if (hash === this.activeRouteHash) {
        this.historyIndex = index;
        return;
      }
      const match = this.viewRegistry.findByRoute(hash.slice(1));
      const navigation = match
        ? this.queueNavigation(match.view.id, match.params, match.query, 'traverse', index, hash)
        : Promise.resolve(false);
      const restore = navigation.then(async navigated => {
        if (navigated || !this.isConnected || window.location.hash !== hash || !this.activeRouteHash) return false;
        const distance = this.historyIndex - index;
        if (distance) {
          await new Promise<void>(resolve => {
            this.restoringHistory = { hash: this.activeRouteHash, resolve };
            window.history.go(distance);
          });
        } else window.history.replaceState(this.routeHistoryState(), '', this.activeRouteHash);
        return false;
      });
      // A cancelled browser traversal must finish restoring before accepting another request.
      this.navigationQueue = restore;
    };
    window.addEventListener('hashchange', handleHashChange);
    this.routerCleanup = () => {
      this.routingGeneration++;
      this.restoringHistory?.resolve();
      this.restoringHistory = undefined;
      window.removeEventListener('hashchange', handleHashChange);
    };
  }

  private buildMainMenuFromSections(config: interfaces.IAppConfig): interfaces.IMenuGroup[] {
    if (!config.mainMenu?.sections) return [];

    return config.mainMenu.sections.map((section) => ({
      name: section.name || '',
      items: section.views
        .map((viewId) => {
          const view = this.viewRegistry.get(viewId);
          if (!view) {
            console.warn(`View "${viewId}" not found in registry`);
            return null;
          }
          return {
            key: view.id,
            label: view.name,
            iconName: view.iconName,
            action: () => this.navigateToView(viewId),
            badge: view.badge,
          } as interfaces.IMenuItem;
        })
        .filter(Boolean) as interfaces.IMenuItem[],
    }));
  }

  private buildBottomTabsFromItems(items: string[]): interfaces.IMenuItem[] {
    return items
      .map((viewId) => {
        const view = this.viewRegistry.get(viewId);
        if (!view) {
          console.warn(`View "${viewId}" not found in registry`);
          return null;
        }
        return {
          key: view.id,
          label: view.name,
          iconName: view.iconName,
          action: () => this.navigateToView(viewId),
        } as interfaces.IMenuItem;
      })
      .filter(Boolean) as interfaces.IMenuItem[];
  }

  private async loadView(
    view: interfaces.IViewDefinition,
    params?: Record<string, string>,
    query?: Record<string, string>
  ): Promise<void> {
    const previousView = this.currentView;

    // Get view container
    const viewContainer = this.maincontent?.querySelector('.view-container')
      || this.shadowRoot?.querySelector('.view-container');

    if (!viewContainer) throw new Error('The app view container is unavailable.');
    if (viewContainer) {
      // Activate view with caching and lifecycle hooks
      const element = await this.viewRegistry.activateView(
        view.id,
        viewContainer as HTMLElement,
        params,
        query
      );

      if (!element) throw new Error('The app screen could not be loaded.');
      this.currentView = view;
      if (element) {
        // Emit lifecycle event
        this.viewLifecycle$.next({
          type: 'activated',
          viewId: view.id,
          element,
          params,
        });
      }
    }

    // Apply view-specific secondary menu
    if (view.secondaryMenu) {
      this.secondarymenuGroups = view.secondaryMenu;
      this.secondarymenuHeading = view.name;
    }

    // Apply view-specific content tabs
    if (view.contentTabs) {
      this.maincontentTabs = view.contentTabs;
    }

    // Update main menu selection
    this.setMainMenuSelection(view.id);

    // Emit view change event
    const changeEvent: interfaces.IViewChangeEvent = {
      viewId: view.id,
      view,
      previousView,
      params,
      query,
    };
    this.viewChanged$.next(changeEvent);

    // Also dispatch DOM event for backwards compatibility
    this.dispatchEvent(
      new CustomEvent('view-change', {
        detail: changeEvent,
        bubbles: true,
        composed: true,
      })
    );
  }

  // ==========================================
  // EVENT HANDLERS (Internal)
  // ==========================================

  private handleAppbarMenuSelect(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent('appbar-menu-select', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }

  private handleAppbarBreadcrumbNavigate(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent('appbar-breadcrumb-navigate', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }

  private handleAppbarSearchClick() {
    this.dispatchEvent(new CustomEvent('appbar-search-click', {
      bubbles: true,
      composed: true
    }));
  }

  private handleAppbarSearchQuery(e: CustomEvent) {
    if (this.searchCallback) {
      this.searchCallback(e.detail.query);
    }
    this.dispatchEvent(new CustomEvent('search-query', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }

  private handleAppbarUserMenuOpen() {
    this.dispatchEvent(new CustomEvent('appbar-user-menu-open', {
      bubbles: true,
      composed: true
    }));
  }

  private handleAppbarProfileMenuSelect(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent('appbar-profile-menu-select', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }

  private handleMainmenuTabSelect(e: CustomEvent) {
    if (this.compact) this.closeCompactMenus();
    this.mainmenuSelectedTab = e.detail.tab;
    this.dispatchEvent(new CustomEvent('mainmenu-tab-select', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }

  private async toggleCompactMenu(menu: 'main' | 'section') {
    const open = menu === 'main' ? !this.compactMainOpen : !this.compactSecondaryOpen;
    this.compactMainOpen = menu === 'main' && open;
    this.compactSecondaryOpen = menu === 'section' && open;
    if (!open) return;
    await this.updateComplete;
    const element = menu === 'main'
      ? this.shadowRoot?.querySelector<DeesAppuiMainmenu>('dees-appui-mainmenu')
      : this.shadowRoot?.querySelector<DeesAppuiSecondarymenu>('dees-appui-secondarymenu');
    await element?.updateComplete;
    element?.shadowRoot
      ?.querySelector<HTMLButtonElement>('button:not(:disabled)')
      ?.focus({ preventScroll: true });
  }

  private closeCompactMenus() {
    const menu = this.compactSecondaryOpen ? 'section' : 'main';
    this.compactMainOpen = false;
    this.compactSecondaryOpen = false;
    this.shadowRoot
      ?.querySelector<HTMLButtonElement>(`[data-menu="${menu}"]`)
      ?.focus({ preventScroll: true });
  }

  private handleSecondarymenuItemSelect(e: CustomEvent) {
    if (this.compact) this.closeCompactMenus();
    this.secondarymenuSelectedItem = e.detail.item;
    this.dispatchEvent(new CustomEvent('secondarymenu-item-select', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }

  private handleMainmenuCollapseChange(e: CustomEvent) {
    if (this.compact) {
      if (e.detail.collapsed) this.closeCompactMenus();
      else this.compactMainOpen = true;
    } else this.mainmenuCollapsed = e.detail.collapsed;
    this.dispatchEvent(new CustomEvent('mainmenu-collapse-change', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }

  private handleSecondarymenuCollapseChange(e: CustomEvent) {
    if (this.compact) {
      if (e.detail.collapsed) this.closeCompactMenus();
      else this.compactSecondaryOpen = true;
    } else this.secondarymenuCollapsed = e.detail.collapsed;
    this.dispatchEvent(new CustomEvent('secondarymenu-collapse-change', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }

  private handleContentTabSelect(e: CustomEvent) {
    this.maincontentSelectedTab = e.detail.tab;
    this.dispatchEvent(new CustomEvent('content-tab-select', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }

  private handleContentTabClose(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent('content-tab-close', {
      detail: e.detail,
      bubbles: true,
      composed: true
    }));
  }
}
